// Portal API Service - endpoints for the sales portal frontend

import api from './api';
import type {
  PortalInboxResponse,
  PortalEmailItem,
  PortalInboxStats,
  SalespersonOption,
  AssignRequest,
  StatusUpdateRequest,
  FlagUpdateRequest,
  RecipientResult,
  ReplyContext,
  SendEmailRequest,
  SendEmailResponse,
  EmailSignature,
  CreateSignatureRequest,
  UpdateSignatureRequest,
  ChainDetail,
  InboxAlertsData,
  PortalClassificationActivity,
  AllSalespeopleResponse,
  SentEmailListResponse,
  SentEmailItem,
  SentKindFilter,
  SentSenderFilter,
  CustomerOverrideResponse,
  CustomerSearchResult,
  CreateCustomerRequest,
  CreateCustomerResponse,
  ShipToSearchResult,
  OrderDocumentsResponse,
  DocumentCountsResponse,
  OrderDocumentCategory,
  OrderUploadedDocumentRead,
} from '../types/portal';

export interface PortalConfig {
  sandbox_mode: boolean;
  environment: string;
  sandbox_toggleable: boolean;
  sandbox_redirect_email: string;
  /** Tech Support Co-Pilot human-send kill-switch (server-side). */
  tech_support_send_enabled: boolean;
  /** Resolved Edit-in-Sage gate — drives Sage-write affordances like the
   * AddCustomerModal "Create in Sage" mode. */
  sage_edit_enabled?: boolean;
}

// ---- Real Sage customer create (POST /portal/customers/sage) ----
export interface SageCustomerCreateRequest {
  customer_name: string;
  /** Omit to default to the logged-in user's Sage salesperson code. */
  salesperson_no?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  zip_code?: string | null;
  /** Portal country code ('US' | 'CA') — drives postal-code validation and
   *  the ZIP/Postal label. Not stored in Sage (no CountryCode on the customer
   *  import); validation/UX only. */
  country_code?: string | null;
  telephone_no?: string | null;
  email_address?: string | null;
  terms_code?: string | null;
  customer_type?: string | null;
  /** Override the exact-name duplicate guard (distinct same-name company). */
  allow_duplicate_name?: boolean;
}

export interface SageCustomerCreateResponse {
  success: boolean;
  /** The server-allocated account number (name prefix + sequence). */
  customer_no?: string | null;
  salesperson_no?: string | null;
  /** 'submitted' = queued to Sage; the reconciler confirms (~2 min). */
  status?: string | null;
  error_message?: string | null;
}

// Dedup cache for getInboxStats — prevents duplicate API calls when
// both PortalLayout and PortalInboxPage call stats on mount.
let _statsCachePromise: Promise<PortalInboxStats> | null = null;
let _statsCacheTs = 0;
const STATS_CACHE_TTL_MS = 5_000;
// Set by invalidateStatsCache(): the next read asks the server to bypass its
// own 30 s memo (GET /portal/inbox/stats?fresh=true) so a count the viewer
// just changed is never answered from before their write. Polls that did
// not follow a mutation (the nav badge) are content with the memo.
let _statsForceFresh = false;

export const portalService = {
  /**
   * Get portal configuration (sandbox mode, environment, etc.)
   */
  getPortalConfig: async (): Promise<PortalConfig> => {
    const response = await api.get<PortalConfig>('/portal/config');
    return response.data;
  },

  /**
   * List inbox emails with optional filtering
   */
  listInboxEmails: async (params?: {
    view?: string;
    search?: string;
    email_id?: string;
    sort_by?: string;
    sort_order?: string;
    offset?: number;
    limit?: number;
    exclude_sent?: boolean;
    read_state?: 'read' | 'unread';
    show_resolved?: boolean;
  }): Promise<PortalInboxResponse> => {
    const response = await api.get<PortalInboxResponse>('/portal/inbox', { params });
    return response.data;
  },

  /**
   * Get inbox statistics (deduped with 5 s TTL cache)
   */
  getInboxStats: async (): Promise<PortalInboxStats> => {
    const now = Date.now();
    if (_statsCachePromise && now - _statsCacheTs < STATS_CACHE_TTL_MS) {
      return _statsCachePromise;
    }
    _statsCacheTs = now;
    const fresh = _statsForceFresh;
    _statsForceFresh = false;
    _statsCachePromise = api
      .get<PortalInboxStats>('/portal/inbox/stats', fresh ? { params: { fresh: true } } : undefined)
      .then((r) => r.data)
      .catch((err) => {
        // Clear cache on error so next call retries
        _statsCachePromise = null;
        throw err;
      });
    return _statsCachePromise;
  },

  /** Force-invalidate the stats cache (call after mutations). The next
   *  read also bypasses the server-side memo. */
  invalidateStatsCache: () => {
    _statsCachePromise = null;
    _statsCacheTs = 0;
    _statsForceFresh = true;
  },

  /**
   * Live AI-drafting counts (emails currently being classified + recent
   * failures). Powers the Order Review banner and inbox polling decisions.
   */
  getClassificationActivity: async (): Promise<PortalClassificationActivity> => {
    const response = await api.get<PortalClassificationActivity>(
      '/portal/inbox/classification-activity',
    );
    return response.data;
  },

  /**
   * Assign an email to a salesperson
   */
  assignEmail: async (emailId: string, data: AssignRequest): Promise<{ message: string }> => {
    const response = await api.post<{ message: string }>(
      `/portal/inbox/${emailId}/assign`,
      data,
    );
    return response.data;
  },

  /**
   * Update email status
   */
  updateEmailStatus: async (
    emailId: string,
    data: StatusUpdateRequest,
  ): Promise<{ message: string }> => {
    const response = await api.patch<{ message: string }>(
      `/portal/inbox/${emailId}/status`,
      data,
    );
    return response.data;
  },

  /**
   * Toggle email flag
   */
  toggleFlag: async (
    emailId: string,
    data: FlagUpdateRequest,
  ): Promise<{ is_flagged: boolean }> => {
    const response = await api.patch<{ is_flagged: boolean }>(
      `/portal/inbox/${emailId}/flag`,
      data,
    );
    return response.data;
  },

  /**
   * Toggle email read/unread status
   */
  toggleReadStatus: async (
    emailId: string,
    isRead: boolean,
  ): Promise<{ email_id: string; is_read: boolean }> => {
    const response = await api.patch<{ email_id: string; is_read: boolean }>(
      `/portal/inbox/${emailId}/read`,
      { is_read: isRead },
    );
    return response.data;
  },

  /**
   * List salespersons for assignment dropdown
   */
  listSalespersons: async (): Promise<SalespersonOption[]> => {
    const response = await api.get<SalespersonOption[]>('/portal/salespersons');
    return response.data;
  },

  // ===========================================================================
  // Attachments
  // ===========================================================================

  /**
   * Get the download URL for an email attachment.
   * Uses the proxy endpoint that fetches from Gmail on the server side.
   */
  getAttachmentUrl: (emailId: string, filename: string): string => {
    return `/api/portal/email/${emailId}/attachment/${encodeURIComponent(filename)}`;
  },

  /**
   * Download an email attachment as a Blob via authenticated request.
   * Uses the axios interceptor to include the JWT token automatically.
   */
  downloadAttachment: async (emailId: string, filename: string): Promise<Blob> => {
    const url = `/portal/email/${emailId}/attachment/${encodeURIComponent(filename)}`;
    const response = await api.get(url, { responseType: 'blob' });
    return response.data as Blob;
  },

  // ===========================================================================
  // Email Classification Feedback
  // ===========================================================================

  /**
   * Submit classification feedback (thumbs up/down) on an email decision.
   * Uses email_id to find the decision — works for all decisions including 'ignore'.
   */
  submitClassificationFeedback: async (
    emailId: string,
    feedback: 'thumbs_up' | 'thumbs_down',
    feedbackText?: string,
  ): Promise<{ email_id: string; feedback: string; knowledge_updated: boolean }> => {
    const response = await api.post<{ email_id: string; feedback: string; knowledge_updated: boolean }>(
      `/portal/inbox/${emailId}/feedback`,
      {
        feedback,
        feedback_text: feedbackText || null,
      },
    );
    return response.data;
  },

  // ===========================================================================
  // Email Chain
  // ===========================================================================

  /**
   * Fetch a single email with full body content (body + body_html).
   * List responses exclude bodies to reduce payload; call this when
   * opening the detail modal.
   */
  getEmailDetail: async (emailId: string): Promise<PortalEmailItem | null> => {
    const response = await api.get<PortalInboxResponse>('/portal/inbox', {
      params: { email_id: emailId },
    });
    return response.data.emails[0] ?? null;
  },

  /**
   * Get the full email chain for a given email.
   * Returns all emails in the same thread, newest first.
   */
  getEmailChain: async (emailId: string): Promise<ChainDetail> => {
    const response = await api.get<ChainDetail>(`/portal/inbox/chain/${emailId}`);
    return response.data;
  },

  // ===========================================================================
  // Email Compose / Reply
  // ===========================================================================

  /**
   * Search for email recipients (autocomplete)
   */
  searchRecipients: async (
    query: string,
    limit: number = 15,
  ): Promise<RecipientResult[]> => {
    if (query.trim().length < 2) return [];
    const response = await api.get<RecipientResult[]>('/portal/email/recipients', {
      params: { q: query, limit },
    });
    return response.data;
  },

  /**
   * Get reply context for composing a reply
   */
  getReplyContext: async (
    emailId: string,
    action: string = 'reply',
  ): Promise<ReplyContext> => {
    const response = await api.get<ReplyContext>(
      `/portal/email/${emailId}/reply-context`,
      { params: { action } },
    );
    return response.data;
  },

  /**
   * Send an email (reply or new)
   */
  sendEmail: async (data: SendEmailRequest): Promise<SendEmailResponse> => {
    const response = await api.post<SendEmailResponse>('/portal/email/send', data);
    return response.data;
  },

  /**
   * Get the account that replies will be sent from
   */
  getSendingAccount: async (): Promise<{ email: string | null }> => {
    const response = await api.get<{ email: string | null }>('/portal/email/sending-account');
    return response.data;
  },

  // ===========================================================================
  // Signatures
  // ===========================================================================

  /**
   * List user's email signatures
   */
  listSignatures: async (): Promise<EmailSignature[]> => {
    const response = await api.get<EmailSignature[]>('/portal/signatures');
    return response.data;
  },

  /**
   * Get or create the default signature for the current user
   */
  getOrCreateDefaultSignature: async (): Promise<EmailSignature> => {
    const response = await api.post<EmailSignature>('/portal/signatures/default');
    return response.data;
  },

  /**
   * Reset the default signature to the corporate-mandate format
   * (re-rendered server-side from the user's name/email/title/phone)
   */
  resetDefaultSignature: async (): Promise<EmailSignature> => {
    const response = await api.post<EmailSignature>('/portal/signatures/default/reset');
    return response.data;
  },

  /**
   * Create a new signature
   */
  createSignature: async (data: CreateSignatureRequest): Promise<EmailSignature> => {
    const response = await api.post<EmailSignature>('/portal/signatures', data);
    return response.data;
  },

  /**
   * Update a signature
   */
  updateSignature: async (
    signatureId: string,
    data: UpdateSignatureRequest,
  ): Promise<EmailSignature> => {
    const response = await api.put<EmailSignature>(
      `/portal/signatures/${signatureId}`,
      data,
    );
    return response.data;
  },

  /**
   * Delete a signature
   */
  deleteSignature: async (signatureId: string): Promise<void> => {
    await api.delete(`/portal/signatures/${signatureId}`);
  },

  /**
   * Upload an image for the email compose editor via GCS signed URL.
   * Returns the public URL of the uploaded image.
   */
  uploadComposeImage: async (file: File): Promise<string> => {
    // 1. Get signed upload URL from the backend
    const initResponse = await api.post<{
      upload_url: string;
      public_url: string;
      content_type: string;
    }>('/portal/email/compose/upload-image', {
      filename: file.name,
      content_type: file.type,
      size_bytes: file.size,
    });

    const { upload_url, public_url, content_type } = initResponse.data;

    // 2. Upload directly to GCS via signed URL.
    const uploadResponse = await fetch(upload_url, {
      method: 'PUT',
      headers: { 'Content-Type': content_type, 'x-goog-acl': 'public-read' },
      body: file,
    });

    if (!uploadResponse.ok) {
      const detail = await uploadResponse.text().catch(() => '');
      throw new Error(
        `Image upload to storage failed (${uploadResponse.status}). ${detail}`.trim(),
      );
    }

    return public_url;
  },

  /**
   * Upload a file attachment for the email compose via GCS signed URL.
   * Returns the attachment metadata for inclusion in the send request.
   */
  uploadEmailAttachment: async (file: File): Promise<{
    file_id: string;
    filename: string;
    content_type: string;
    size_bytes: number;
    url: string;
  }> => {
    // 1. Get signed upload URL from the backend
    const initResponse = await api.post<{
      file_id: string;
      upload_url: string;
      public_url: string;
      filename: string;
      content_type: string;
      size_bytes: number;
    }>('/portal/email/compose/upload-attachment', {
      filename: file.name,
      content_type: file.type,
      size_bytes: file.size,
    });

    const { file_id, upload_url, public_url, filename, content_type, size_bytes } = initResponse.data;

    // 2. Upload directly to GCS via signed URL.
    const uploadResponse = await fetch(upload_url, {
      method: 'PUT',
      headers: { 'Content-Type': content_type, 'x-goog-acl': 'public-read' },
      body: file,
    });

    if (!uploadResponse.ok) {
      const detail = await uploadResponse.text().catch(() => '');
      throw new Error(
        `Attachment upload failed (${uploadResponse.status}). ${detail}`.trim(),
      );
    }

    return { file_id, filename, content_type, size_bytes, url: public_url };
  },

  /**
   * Upload a signature image via GCS signed URL.
   * Returns the public URL of the uploaded image.
   */
  uploadSignatureImage: async (file: File): Promise<string> => {
    // 1. Get signed upload URL from the backend
    const initResponse = await api.post<{
      upload_url: string;
      public_url: string;
      content_type: string;
    }>('/portal/signatures/upload-image', {
      filename: file.name,
      content_type: file.type,
      size_bytes: file.size,
    });

    const { upload_url, public_url, content_type } = initResponse.data;

    // 2. Upload directly to GCS via signed URL.
    // Use the content_type from the signed URL response to ensure an exact match
    // with the signed URL's Content-Type requirement.
    const uploadResponse = await fetch(upload_url, {
      method: 'PUT',
      headers: { 'Content-Type': content_type, 'x-goog-acl': 'public-read' },
      body: file,
    });

    if (!uploadResponse.ok) {
      const detail = await uploadResponse.text().catch(() => '');
      throw new Error(
        `Image upload to storage failed (${uploadResponse.status}). ${detail}`.trim(),
      );
    }

    return public_url;
  },

  // ===========================================================================
  // Sent Emails
  // ===========================================================================

  /**
   * List sent emails from the provider (Gmail SENT label)
   */
  listSentEmails: async (params?: {
    search?: string;
    sender?: SentSenderFilter;
    /** Omitted = the backend default, 'person'. */
    kind?: SentKindFilter;
    sort_order?: 'asc' | 'desc';
    offset?: number;
    limit?: number;
  }): Promise<SentEmailListResponse> => {
    const response = await api.get<SentEmailListResponse>('/portal/sent', { params });
    return response.data;
  },

  /**
   * Get full detail for a sent email including body content
   */
  getSentEmailDetail: async (emailId: string): Promise<SentEmailItem> => {
    const response = await api.get<SentEmailItem>(`/portal/sent/${emailId}`);
    return response.data;
  },

  // ===========================================================================
  // Customer Override
  // ===========================================================================

  /**
   * Search customers by name or number for the typeahead dropdown.
   * Reuses the existing /customers endpoint.
   */
  searchCustomers: async (
    query: string,
    limit: number = 10,
  ): Promise<CustomerSearchResult[]> => {
    if (query.trim().length < 2) return [];
    const response = await api.get<{ customers: CustomerSearchResult[] }>(
      '/customers',
      { params: { search: query, limit } },
    );
    return response.data.customers;
  },

  /**
   * Override the matched customer on an email.
   * Auto-derives salesperson from the customer.
   *
   * The response carries `linked_orders` — the email's linked, thread-scoped
   * order(s) classified for the reassignment → order safety-valve. Pass
   * `updateLinkedOrders: true` to also correct the customer on the editable
   * (pre-Sage) ones; orders already in Sage are reported but never changed.
   */
  overrideCustomer: async (
    emailId: string,
    customerNo: string,
    opts?: { updateLinkedOrders?: boolean },
  ): Promise<CustomerOverrideResponse> => {
    const response = await api.patch<CustomerOverrideResponse>(
      `/portal/inbox/${emailId}/customer`,
      {
        customer_no: customerNo,
        update_linked_orders: opts?.updateLinkedOrders ?? false,
      },
    );
    return response.data;
  },

  /**
   * Clear the manual customer override on an email.
   */
  clearCustomerOverride: async (
    emailId: string,
  ): Promise<{ message: string; email_id: string }> => {
    const response = await api.delete<{ message: string; email_id: string }>(
      `/portal/inbox/${emailId}/customer`,
    );
    return response.data;
  },

  /**
   * Create a new customer from the portal.
   * Sets source='manual' so it won't be overwritten by Sage sync.
   */
  /**
   * Create a REAL Sage customer (AR_Customer) — the account number is
   * allocated server-side and the write rides Adam's audited cloud->Sage
   * customer path. 409 = exact-name duplicate (overridable); 400 = invalid
   * salesperson / inconsistent state+ZIP; 403 = feature flag off.
   */
  createSageCustomer: async (
    data: SageCustomerCreateRequest,
  ): Promise<SageCustomerCreateResponse> => {
    const response = await api.post<SageCustomerCreateResponse>(
      '/portal/customers/sage',
      data,
    );
    return response.data;
  },

  createCustomer: async (
    data: CreateCustomerRequest,
  ): Promise<CreateCustomerResponse> => {
    const response = await api.post<CreateCustomerResponse>(
      '/portal/customers',
      data,
    );
    return response.data;
  },

  // ===========================================================================
  // Ship-To Address Search
  // ===========================================================================

  /**
   * Search ship-to addresses for a specific customer.
   */
  searchShipTo: async (
    customerNo: string,
    query: string,
    limit: number = 10,
  ): Promise<ShipToSearchResult[]> => {
    const response = await api.get<ShipToSearchResult[]>(
      `/portal/customers/${customerNo}/ship-to-addresses`,
      { params: { q: query, limit } },
    );
    return response.data;
  },

  /**
   * Search ship-to addresses across ALL customers.
   */
  searchAllShipTo: async (
    query: string,
    limit: number = 10,
  ): Promise<ShipToSearchResult[]> => {
    const response = await api.get<ShipToSearchResult[]>(
      '/portal/ship-to-addresses/search',
      { params: { q: query, limit } },
    );
    return response.data;
  },

  // ===========================================================================
  // Trash (Mark as Ignored)
  // ===========================================================================

  /**
   * Mark an email as trash (creates an IGNORE decision).
   * Trashed emails are excluded from the unmapped view.
   *
   * Order-related / Important emails are server-guarded: without
   * `confirmGuarded` the request rejects with a 409 `trash_guard` payload
   * (see utils/trashGuard.ts) so the UI can show the confirm dialog.
   */
  markAsTrash: async (
    emailId: string,
    opts?: { confirmGuarded?: boolean },
  ): Promise<{ message: string; email_id: string }> => {
    const qs = opts?.confirmGuarded ? '?confirm_guarded=true' : '';
    const response = await api.post<{ message: string; email_id: string }>(
      `/portal/inbox/${emailId}/trash${qs}`,
    );
    return response.data;
  },

  /**
   * Restore an email from trash (removes the IGNORE decision).
   */
  untrash: async (
    emailId: string,
  ): Promise<{ message: string; email_id: string }> => {
    const response = await api.delete<{ message: string; email_id: string }>(
      `/portal/inbox/${emailId}/trash`,
    );
    return response.data;
  },

  /**
   * Bulk-mark multiple emails as trash.
   *
   * Order-related / Important emails are server-guarded: without
   * `confirmGuarded` the request rejects with a 409 `trash_guard` payload
   * listing every guarded email (see utils/trashGuard.ts) and nothing is
   * trashed.
   */
  bulkMarkAsTrash: async (
    emailIds: string[],
    opts?: { confirmGuarded?: boolean },
  ): Promise<{ message: string; trashed: number; skipped: number }> => {
    const response = await api.post<{
      message: string;
      trashed: number;
      skipped: number;
    }>('/portal/inbox/bulk-trash', {
      email_ids: emailIds,
      confirm_guarded: opts?.confirmGuarded ?? false,
    });
    return response.data;
  },

  // ===========================================================================
  // Bulk Dismiss Action Items (mark-as-resolved)
  // ===========================================================================

  /**
   * Bulk-dismiss action items linked to multiple emails.
   */
  bulkDismissActionItems: async (
    emailIds: string[],
  ): Promise<{ message: string; dismissed: number; skipped: number }> => {
    const response = await api.post<{
      message: string;
      dismissed: number;
      skipped: number;
    }>('/portal/inbox/bulk-dismiss', { email_ids: emailIds });
    return response.data;
  },

  // ===========================================================================
  // Dismiss / Undismiss Action Item (via email)
  // ===========================================================================

  /**
   * Dismiss the action item linked to an email (mark as resolved).
   */
  dismissActionItem: async (
    emailId: string,
  ): Promise<{ message: string; email_id: string; notification_id: string }> => {
    const response = await api.patch<{
      message: string;
      email_id: string;
      notification_id: string;
    }>(`/portal/inbox/${emailId}/dismiss-action`);
    return response.data;
  },

  /**
   * Undismiss the action item linked to an email (undo resolve).
   */
  undismissActionItem: async (
    emailId: string,
  ): Promise<{ message: string; email_id: string; notification_id: string }> => {
    const response = await api.patch<{
      message: string;
      email_id: string;
      notification_id: string;
    }>(`/portal/inbox/${emailId}/undismiss-action`);
    return response.data;
  },

  // ===========================================================================
  // Inbox Alerts & Salespeople Management
  // ===========================================================================

  /**
   * Get inbox alert counts (unmapped emails, unassigned salespeople)
   */
  getInboxAlerts: async (): Promise<InboxAlertsData> => {
    const response = await api.get<InboxAlertsData>('/portal/my-salespeople/alerts');
    return response.data;
  },

  /**
   * Get all salespeople with assignment status
   */
  getAllSalespeople: async (): Promise<AllSalespeopleResponse> => {
    const response = await api.get<AllSalespeopleResponse>('/portal/my-salespeople/all');
    return response.data;
  },

  /**
   * Assign a salesperson code to the current user
   */
  assignSalesperson: async (
    managedSalespersonNo: string,
  ): Promise<{ success: boolean; message: string }> => {
    const response = await api.post<{ success: boolean; message: string }>(
      '/portal/my-salespeople/assign',
      { managed_salesperson_no: managedSalespersonNo },
    );
    return response.data;
  },

  /**
   * Unassign a salesperson code from the current user
   */
  unassignSalesperson: async (
    managedSalespersonNo: string,
  ): Promise<{ success: boolean; message: string }> => {
    const response = await api.post<{ success: boolean; message: string }>(
      '/portal/my-salespeople/unassign',
      { managed_salesperson_no: managedSalespersonNo },
    );
    return response.data;
  },
  // Order document aggregation
  getOrderDocuments: async (orderId: string): Promise<OrderDocumentsResponse> => {
    const response = await api.get<OrderDocumentsResponse>(
      `/portal/orders/${orderId}/documents`,
    );
    return response.data;
  },

  getDocumentCounts: async (orderIds: string[]): Promise<DocumentCountsResponse> => {
    const response = await api.post<DocumentCountsResponse>(
      '/portal/orders/document-counts',
      { order_ids: orderIds },
    );
    return response.data;
  },

  getCustomerDocuments: async (
    customerNo: string,
  ): Promise<OrderDocumentsResponse> => {
    const response = await api.get<OrderDocumentsResponse>(
      `/portal/customers/${encodeURIComponent(customerNo)}/documents`,
    );
    return response.data;
  },

  uploadOrderDocument: async (
    orderId: string,
    file: File,
    category: OrderDocumentCategory = 'general',
  ): Promise<OrderUploadedDocumentRead> => {
    const form = new FormData();
    form.append('file', file);
    form.append('category', category);
    const response = await api.post<OrderUploadedDocumentRead>(
      `/portal/orders/${orderId}/documents/upload`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  },

  uploadCustomerDocument: async (
    customerNo: string,
    file: File,
    category: OrderDocumentCategory = 'general',
  ): Promise<OrderUploadedDocumentRead> => {
    const form = new FormData();
    form.append('file', file);
    form.append('category', category);
    const response = await api.post<OrderUploadedDocumentRead>(
      `/portal/customers/${encodeURIComponent(customerNo)}/documents/upload`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  },

  deleteUploadedDocument: async (
    documentId: string,
  ): Promise<OrderUploadedDocumentRead> => {
    const response = await api.delete<OrderUploadedDocumentRead>(
      `/portal/uploaded-documents/${documentId}`,
    );
    return response.data;
  },
};

export default portalService;
